import type { ComponentProps, ReactNode } from 'react';

import { cn } from '@repo/lib/cn';
import { ChevronDown, ChevronUp, MinusIcon, PlusIcon } from 'lucide-react';
import { createContext, use, useEffect, useState } from 'react';

import { Button } from '@/components/ui/button';
import { ButtonGroup } from '@/components/ui/button/ButtonGroup';
import { InputGroup, InputGroupAddon, InputGroupInput } from '@/components/ui/input-group';

import type { NumberInputContextValue, NumberInputProps, NumberInputProviderProps } from '.';

const NumberInputContext = createContext<NumberInputContextValue | null>(null);

export function useNumberInputContext() {
  const context = use(NumberInputContext);
  if (!context) {
    throw new Error('useNumberInputContext must be used within a NumberInputProvider');
  }
  return context;
}

export function NumberInputProvider(numberInputProviderProps: NumberInputProviderProps) {
  const { value: propValue, min, max, step = 1, onChange, disabled = false, children, ...props } = numberInputProviderProps;

  const [value, setValue] = useState<number | undefined>(propValue);

  useEffect(() => {
    setValue(propValue);
  }, [propValue]);

  const handleIncrement = () => {
    const currentValue: number = value ?? 0;
    const newValue: number = currentValue + step;
    if (max === undefined || newValue <= max) {
      setValue(newValue);
      onChange?.(newValue);
    }
  };

  const handleDecrement = () => {
    const currentValue: number = value ?? 0;
    const newValue: number = currentValue - step;
    if (min === undefined || newValue >= min) {
      setValue(newValue);
      onChange?.(newValue);
    }
  };

  const handleInputChange = (e: React.ChangeEvent<HTMLInputElement>) => {
    const inputValue: string = e.target.value;
    if (inputValue === '') {
      onChange?.(undefined);
      setValue(undefined);
      return;
    }

    const newValue: number = Number.parseFloat(inputValue);
    if (!isNaN(newValue)) {
      if ((min === undefined || newValue >= min) && (max === undefined || newValue <= max)) {
        setValue(newValue);
        onChange?.(newValue);
      }
    }
  };

  const isDecrementDisabled: boolean = disabled || (min !== undefined && (value ?? 0) <= min);
  const isIncrementDisabled: boolean = disabled || (max !== undefined && (value ?? 0) >= max);

  const contextValue: NumberInputContextValue = {
    value,
    min,
    max,
    step,
    disabled,
    isDecrementDisabled,
    isIncrementDisabled,
    handleIncrement,
    handleDecrement,
    handleInputChange,
  };

  return <NumberInputContext.Provider value={{ ...props, ...contextValue }}>{children}</NumberInputContext.Provider>;
}
export function NumberInput(NumberInputProps: NumberInputProps) {
  const { children, className, ...props } = NumberInputProps;
  return (
    <NumberInputProvider {...props}>
      <ButtonGroup className={cn('w-full', className)}>{children}</ButtonGroup>
    </NumberInputProvider>
  );
}

export function NumberInputField(
  numberInputFieldProps: ComponentProps<typeof InputGroupInput> & {
    children?: ReactNode;
  }
) {
  const { className, children, ...props } = numberInputFieldProps;

  const {
    value,
    min,
    max,
    step,
    disabled,
    handleInputChange,
    isDecrementDisabled: _isDecrementDisabled,
    isIncrementDisabled: _isIncrementDisabled,
    handleIncrement: _handleIncrement,
    handleDecrement: _handleDecrement,
    ...parentProps
  } = useNumberInputContext();

  return (
    <InputGroup>
      <InputGroupInput
        {...parentProps}
        {...props}
        type="number"
        value={value ?? ''}
        onChange={handleInputChange}
        disabled={disabled}
        min={min}
        max={max}
        step={step}
        className={cn('[appearance:textfield] text-center [&::-webkit-inner-spin-button]:appearance-none [&::-webkit-outer-spin-button]:appearance-none', className)}
      />
      {children}
    </InputGroup>
  );
}

export function NumberInputIncrementButton(numberInputIncrementButtonProps: ComponentProps<typeof Button>) {
  const { className, children, ...props } = numberInputIncrementButtonProps;
  const { isIncrementDisabled, handleIncrement } = useNumberInputContext();

  return (
    <Button type="button" variant={'ghost'} {...props} onClick={handleIncrement} disabled={isIncrementDisabled} className={className}>
      {children || <PlusIcon />}
    </Button>
  );
}

export function NumberInputDecrementButton(numberInputDecrementButtonProps: ComponentProps<typeof Button>) {
  const { className, children, ...props } = numberInputDecrementButtonProps;
  const { isDecrementDisabled, handleDecrement } = useNumberInputContext();

  return (
    <Button type="button" variant={'ghost'} {...props} onClick={handleDecrement} disabled={isDecrementDisabled} className={className}>
      {children || <MinusIcon />}
    </Button>
  );
}

export function NumberInputHorizontal(numberInputProps: NumberInputProps) {
  const { className, ...props } = numberInputProps;
  return (
    <NumberInput {...props}>
      <NumberInputDecrementButton variant={'outline'} />
      <NumberInputField className={cn('text-center', className)} />
      <NumberInputIncrementButton variant={'outline'} />
    </NumberInput>
  );
}

export function NumberInputVertical(numberInputProps: NumberInputProps) {
  const { className, ...props } = numberInputProps;
  return (
    <NumberInput {...props}>
      <NumberInputField className={cn('w-fit rounded-r-none pr-0 text-start', className)}>
        <InputGroupAddon align="inline-end" className="flex flex-col gap-0">
          <NumberInputIncrementButton className="h-4 min-h-4 w-4 min-w-4">
            <ChevronUp className="size-3" />
          </NumberInputIncrementButton>
          <NumberInputDecrementButton className="h-4 min-h-4 w-4 min-w-4">
            <ChevronDown className="size-3" />
          </NumberInputDecrementButton>
        </InputGroupAddon>
      </NumberInputField>
      {props.max !== undefined && (
        <Button variant={'outline'} className={'text-muted-foreground'} type="button">
          /{props.max.toFixed(1)}
        </Button>
      )}
    </NumberInput>
  );
}
